import type { CaptureKind, Item } from '@mystiq/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client.ts';
import { keys } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { ICON, Upload as UploadIcon } from '../icons.ts';
import { sha256Hex } from '../lib/hash.ts';

/** Uploads files into a folder: begin → PUT bytes → complete. Returns per-file results. Recordings can say what they are for. */
export function useUploader(ws: string, folderId: string | undefined, capture?: CaptureKind) {
  const api = useApi();
  const client = useQueryClient();
  const [active, setActive] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  async function uploadOne(file: File): Promise<Item | null> {
    setActive((list) => [...list, file.name]);
    try {
      const sha256 = await sha256Hex(file);
      const input = {
        filename: file.name,
        sizeBytes: file.size,
        // One key for every try at this file: trying again continues the same upload, keeping the parts already sent.
        idempotencyKey: crypto.randomUUID(),
        ...(folderId ? { folderId } : {}),
        ...(file.type ? { mimeType: file.type } : {}),
        ...(sha256 ? { sha256 } : {}),
        ...(capture ? { capture } : {}),
      };
      for (let round = 0; ; round++) {
        const upload = await api.beginUpload(ws, input);
        try {
          await api.putUploadBytes(upload, file);
          return await api.completeUpload(ws, upload.uploadId);
        } catch (error) {
          // Already complete (its answer was lost): that is the file.
          if (error instanceof ApiError && error.code === 'CONFLICT') {
            const item = await api.completeUpload(ws, upload.uploadId).catch(() => null);
            if (item) return item;
          }
          // A dropped connection, or a link that ran out during a long upload: a fresh link, and carry on.
          const resumable = error instanceof ApiError && (error.code === 'NETWORK' || error.code === 'UNAUTHENTICATED');
          if (!resumable || round >= 2) throw error;
        }
      }
    } catch (error) {
      setErrors((list) => [...list, `${file.name}: ${(error as Error).message}`]);
      return null;
    } finally {
      setActive((list) => list.filter((name) => name !== file.name));
      void client.invalidateQueries({ queryKey: keys.ws(ws) });
    }
  }

  return {
    active,
    errors,
    clearErrors: () => setErrors([]),
    // Three at a time: many large files at once would only compete for the same connection.
    upload: (files: FileList | File[]) => inTurns([...files], 3, uploadOne),
  };
}

/** Runs fn for each value, at most `limit` at a time; results keep the order of the values. */
async function inTurns<T, R>(values: T[], limit: number, fn: (value: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(values.length);
  let next = 0;
  const lane = async () => {
    while (next < values.length) {
      const index = next++;
      results[index] = await fn(values[index]!);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, values.length) }, lane));
  return results;
}

export function UploadButton({ onFiles, label = 'Upload', accept }: { onFiles(files: FileList): void; label?: string; accept?: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button className="button" onClick={() => input.current?.click()}>
        <UploadIcon size={ICON.nav} stroke={ICON.stroke} />
        {label}
      </button>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        {...(accept ? { accept } : {})}
        data-testid="file-input"
        onChange={(event) => {
          if (event.target.files?.length) onFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </>
  );
}

export function DropZone({ onFiles, children }: { onFiles(files: FileList): void; children: ReactNode }) {
  const [dragging, setDragging] = useState(false);
  return (
    <div
      className={`dropzone${dragging ? ' dragging' : ''}`}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        if (event.dataTransfer.files.length) onFiles(event.dataTransfer.files);
      }}
    >
      {children}
    </div>
  );
}

export function UploadStatus({ active, errors, onDismiss }: { active: string[]; errors: string[]; onDismiss(): void }) {
  if (!active.length && !errors.length) return null;
  return (
    <div className="stack" role="status">
      {active.map((name) => (
        <p key={name} className="meta">
          Uploading {name}…
        </p>
      ))}
      {errors.map((message) => (
        <p key={message}>
          <span className="state">Upload failed.</span> {message}{' '}
          <button className="link-button" onClick={onDismiss}>
            Dismiss
          </button>
        </p>
      ))}
    </div>
  );
}
